import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../../../../ui/canon/Button';

// RJ Seller Visibility Workflow — one-shot intro modal.
// Fires the first time a succession user lands on a page that mounts this
// component (LaunchPad or /succession/settings), after the tenant has enabled
// `rj_seller_visibility`. Dismissal is persisted in localStorage — a given
// user sees it at most once per device (Ashley confirmed the once-per-user
// pattern is reasonable in the 2026-07-20 review).
//
// The component is a pure client-side gate: it queries localStorage on mount
// and skips render if the key is present. Server-side tracking would be
// overkill for a first-run tour.
//
// See docs/plans/2026-07-17-001-feat-rj-seller-visibility-workflow-plan.md
const STORAGE_KEY_PREFIX = 'rj_visibility_first_run_shown_v1_';

const RjVisibilityFirstRunModal = ({ userId, enabled, onDismiss }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!enabled || !userId) return;
    try {
      const key = `${STORAGE_KEY_PREFIX}${userId}`;
      if (!localStorage.getItem(key)) {
        setOpen(true);
      }
    } catch (e) {
      // localStorage inaccessible (private mode, SSR, etc.) — silently skip.
    }
  }, [enabled, userId]);

  const dismiss = (opts = {}) => {
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, new Date().toISOString());
    } catch (e) {
      // ignore
    }
    if (typeof window !== 'undefined' && window.ahoy) {
      window.ahoy.track('rj_visibility_first_run_dismissed', { action: opts.action || 'close' });
    }
    setOpen(false);
    onDismiss && onDismiss(opts);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border rounded-lg max-w-lg w-full shadow-xl">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Meet buyers on your terms</h3>
        </div>
        <div className="p-6 space-y-4 text-sm text-gray-900 leading-relaxed">
          <p>
            Your firm has enabled <strong>Marketplace Visibility</strong> — an opt-in way for
            approved growth advisors to reach out to you directly.
          </p>
          <p>
            When you turn visibility on, buyers in your division can see your profile in
            search and send you a one-time introduction message. You stay in control:
            respond, invite the buyer into a private Meeting Room with an NDA, or end the
            conversation. <strong>You can turn visibility off at any time from your settings.</strong>
          </p>
          <p className="text-gray-500 text-xs">
            Visibility is off by default. Open Settings when you're ready to opt in.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-succession-border flex justify-end gap-3">
          <Button variant="outline" size="md" onClick={() => dismiss({ action: 'close' })}>
            Not now
          </Button>
          <Button
            as="a"
            variant="primary"
            size="md"
            href="/succession/settings"
            onClick={() => dismiss({ action: 'go_to_settings' })}
            className="no-underline"
          >
            Go to Settings
          </Button>
        </div>
      </div>
    </div>
  );
};

RjVisibilityFirstRunModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Should be true only when: the tenant has rj_seller_visibility on AND
  // the current user is a succession user. Caller handles the compound gate.
  enabled: PropTypes.bool,
  onDismiss: PropTypes.func,
};

RjVisibilityFirstRunModal.defaultProps = {
  enabled: false,
};

export default RjVisibilityFirstRunModal;
